import { ReactNode } from "react";
import { Link, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { Building2 } from "lucide-react";
import PublicLayout from "../layout/PublicLayout";
import TherapistAvatar from "../components/TherapistAvatar";
import WeeklyHours from "../components/WeeklyHours";
import { usePublicProfile } from "../../application/usePublicDirectory";
import { DIRECTORY_PATH } from "../../domain/paths";

/** A titled block. Rendered only when it has something in it — a heading over
 *  an empty section tells the reader nothing and looks broken. */
function Block({ title, children }: { title: string; children: ReactNode }) {
    return (
        <section className="flex flex-col gap-3 border-t border-gray-100 dark:border-gray-800 pt-6">
            <h2 className="text-theme-xs font-medium uppercase tracking-wider text-content-tertiary">
                {title}
            </h2>
            {children}
        </section>
    );
}

export default function PublicProfilePage() {
    const { t } = useTranslation();
    const { slug } = useParams<{ slug: string }>();
    const { data: profile, isLoading, isError } = usePublicProfile(slug);

    if (isLoading) {
        return (
            <PublicLayout>
                <p className="text-theme-sm text-content-tertiary">{t("common.loading")}</p>
            </PublicLayout>
        );
    }

    // Unpublished and non-existent are the same answer, here as on the server.
    if (isError || !profile) {
        return (
            <PublicLayout>
                <div className="flex flex-col items-start gap-4 py-10">
                    <h1 className="text-title-sm font-bold text-content-primary">
                        {t("publicDirectory.notFound.title")}
                    </h1>
                    <p className="text-content-secondary">
                        {t("publicDirectory.notFound.body")}
                    </p>
                    <Link
                        to={DIRECTORY_PATH}
                        className="rounded-lg bg-brand-500 px-5 py-2.5 font-semibold text-white hover:bg-brand-600"
                    >
                        {t("publicDirectory.notFound.backToSearch")}
                    </Link>
                </div>
            </PublicLayout>
        );
    }

    const chips = [
        profile.city,
        ...profile.modalities.map((m) => t(`publicDirectory.modality.${m}`)),
        ...profile.languages.map((l) => t(`publicDirectory.languages.${l}`, l)),
    ].filter(Boolean) as string[];

    return (
        <PublicLayout>
            <article className="flex flex-col gap-6">
                <header className="flex flex-wrap items-start gap-5">
                    <TherapistAvatar therapist={profile} size="lg" />
                    <div className="min-w-0">
                        <h1 className="text-title-sm font-bold tracking-tight text-content-primary">
                            {profile.display_name}
                        </h1>
                        {profile.headline && (
                            <p className="mt-1 font-medium text-brand-600 dark:text-brand-400">
                                {profile.headline}
                            </p>
                        )}
                        {profile.center_name && (
                            <p className="mt-1 flex items-center gap-1.5 text-theme-sm text-content-secondary">
                                <Building2 className="h-4 w-4 shrink-0" aria-hidden="true" />
                                {t("publicDirectory.worksAt", { center: profile.center_name })}
                            </p>
                        )}
                        {chips.length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-1.5">
                                {chips.map((chip) => (
                                    <span
                                        key={chip}
                                        className="rounded-md bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-theme-xs text-content-secondary"
                                    >
                                        {chip}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </header>

                {profile.public_bio && (
                    <Block title={t("publicDirectory.blocks.about")}>
                        <p className="whitespace-pre-line text-content-secondary">
                            {profile.public_bio}
                        </p>
                    </Block>
                )}

                {/* Catalogue entries are codes to translate; the custom ones are
                    already the therapist's words and are printed as typed. They
                    render identically, because the distinction is ours and not
                    something a visitor should have to notice. */}
                {profile.presenting_problems.length +
                    profile.custom_presenting_problems.length >
                    0 && (
                    <Block title={t("publicDirectory.blocks.worksWith")}>
                        <div className="flex flex-wrap gap-1.5">
                            {profile.presenting_problems.map((problem) => (
                                <span
                                    key={problem}
                                    className="rounded-md bg-brand-500 px-2.5 py-1 text-theme-sm font-medium text-white"
                                >
                                    {t(`publicDirectory.problems.${problem}`, problem)}
                                </span>
                            ))}
                            {profile.custom_presenting_problems.map((label) => (
                                <span
                                    key={label}
                                    className="rounded-md bg-brand-500 px-2.5 py-1 text-theme-sm font-medium text-white"
                                >
                                    {label}
                                </span>
                            ))}
                        </div>
                    </Block>
                )}

                {(profile.therapy_types ?? []).length +
                    (profile.custom_therapy_types ?? []).length >
                    0 && (
                    <Block title={t("publicDirectory.blocks.therapyTypes")}>
                        <div className="flex flex-wrap gap-1.5">
                            {(profile.therapy_types ?? []).map((type) => (
                                <span
                                    key={type}
                                    className="rounded-md bg-brand-50 dark:bg-brand-950 px-2.5 py-1 text-theme-sm text-brand-700 dark:text-brand-300"
                                >
                                    {t(`session.therapyOptions.${type}`, type)}
                                </span>
                            ))}
                            {(profile.custom_therapy_types ?? []).map((label) => (
                                <span
                                    key={label}
                                    className="rounded-md bg-brand-50 dark:bg-brand-950 px-2.5 py-1 text-theme-sm text-brand-700 dark:text-brand-300"
                                >
                                    {label}
                                </span>
                            ))}
                        </div>
                    </Block>
                )}

                {profile.weekly_windows && profile.weekly_windows.length > 0 && (
                    <Block title={t("publicDirectory.blocks.hours")}>
                        <WeeklyHours
                            windows={profile.weekly_windows}
                            timezone={profile.timezone}
                        />
                    </Block>
                )}

                {profile.locations && profile.locations.length > 0 && (
                    <Block title={t("publicDirectory.blocks.locations")}>
                        <ul className="flex flex-col gap-3">
                            {profile.locations.map((location) => (
                                <li key={location.name} className="flex gap-3">
                                    <svg
                                        aria-hidden="true"
                                        className="mt-0.5 shrink-0 text-brand-500"
                                        width="17"
                                        height="17"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                    >
                                        <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                                        <circle cx="12" cy="10" r="3" />
                                    </svg>
                                    <span>
                                        <span className="block font-semibold text-content-primary">
                                            {location.name}
                                        </span>
                                        {location.address && (
                                            <span className="text-theme-sm text-content-tertiary">
                                                {location.address}
                                            </span>
                                        )}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </Block>
                )}

                {(profile.public_email || profile.public_phone || profile.public_website) && (
                    <Block title={t("publicDirectory.blocks.contact")}>
                        <ul className="flex flex-col gap-2">
                            {profile.public_email && (
                                <li>
                                    <a
                                        href={`mailto:${profile.public_email}`}
                                        className="break-all text-brand-600 dark:text-brand-400 hover:underline"
                                    >
                                        {profile.public_email}
                                    </a>
                                </li>
                            )}
                            {profile.public_phone && (
                                <li>
                                    <a
                                        href={`tel:${profile.public_phone.replace(/\s/g, "")}`}
                                        className="text-brand-600 dark:text-brand-400 hover:underline"
                                    >
                                        {profile.public_phone}
                                    </a>
                                </li>
                            )}
                            {profile.public_website && (
                                <li>
                                    <a
                                        href={profile.public_website}
                                        target="_blank"
                                        rel="noopener noreferrer nofollow"
                                        className="break-all text-brand-600 dark:text-brand-400 hover:underline"
                                    >
                                        {profile.public_website}
                                    </a>
                                </li>
                            )}
                        </ul>
                    </Block>
                )}

                {/* A therapist who published no contact details leaves the
                    reader with nowhere to go. The way back to the search is
                    that way out, and it also keeps a sparse profile from
                    trailing off into empty page. */}
                <footer className="border-t border-gray-100 dark:border-gray-800 pt-6">
                    <Link
                        to={DIRECTORY_PATH}
                        className="text-theme-sm font-medium text-brand-600 dark:text-brand-400 hover:underline"
                    >
                        {t("publicDirectory.backToSearch")}
                    </Link>
                </footer>
            </article>
        </PublicLayout>
    );
}
